import { authorizedFetch } from "@/lib/auth/authorized-fetch";
import { uploadFilesToKnowledgeLake } from "@/lib/knowledge/client/upload-knowledge";

export type WizardIngestResult = {
    data?: unknown;
    documentIds?: string[];
};

/**
 * Wizard ingest pipeline:
 * 1. Save each file to Documents (knowledge lake, sync ingest, one file per request)
 * 2. Analyze via JSON using stored document IDs (no large multipart to /api/ingest)
 */
export async function ingestWizardStagedFiles(
    stagedFiles: File[],
    companyId?: string,
): Promise<WizardIngestResult> {
    if (!stagedFiles.length) {
        throw new Error("No files are ready for analysis.");
    }

    if (!companyId) {
        throw new Error("Select a company before analyzing documents.");
    }

    const upload = await uploadFilesToKnowledgeLake(stagedFiles, {
        companyId,
        sourceLabel: "Strategy wizard",
        sync: true,
    });

    if (!upload.documentIds.length) {
        throw new Error("Files were accepted but not stored in Documents. Please retry.");
    }

    const analyzeResponse = await authorizedFetch("/api/wizard/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            companyId,
            documentIds: upload.documentIds,
        }),
    });

    const analyzeJson = (await analyzeResponse.json().catch(() => ({}))) as {
        success?: boolean;
        error?: string;
        data?: unknown;
    };

    if (!analyzeResponse.ok || !analyzeJson.success) {
        const message =
            analyzeJson.error ||
            (analyzeResponse.status === 401
                ? "Your session expired. Please sign in again and retry."
                : "Analysis failed");
        throw new Error(message);
    }

    return {
        data: analyzeJson.data,
        documentIds: upload.documentIds,
    };
}
